import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import AddressCard from '../components/AddressCard';
import AddressForm from '../components/AddressForm';
import './CheckoutPage.css';


const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const subtotal = cart?.totalAmount || 0;
  const shipping = subtotal >= 499 ? 0 : 60;
  const total = subtotal + shipping;

  React.useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const res = await api.get('/addresses');
        setAddresses(res.data.addresses);
        const defaultAddr = res.data.addresses.find(a => a.isDefault);
        if (defaultAddr) setSelectedAddress(defaultAddr);
        else if (res.data.addresses.length > 0) setSelectedAddress(res.data.addresses[0]);
      } catch (err) {
        console.error('Failed to fetch addresses', err);
      }
    };
    fetchAddresses();
  }, []);

  const handleAddressSubmit = async (formData) => {
    try {
      const res = await api.post('/addresses', formData);
      toast.success('Address added successfully');
      setShowAddressForm(false);
      
      // Refresh addresses and select the new one
      const fetchRes = await api.get('/addresses');
      setAddresses(fetchRes.data.addresses);
      setSelectedAddress(fetchRes.data.addresses.find(a => a._id === res.data.address._id) || res.data.address);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save address');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!cart?.items?.length) { toast.error('Your cart is empty'); return; }
    if (!selectedAddress) { toast.error('Please select a delivery address'); return; }
    setLoading(true);
    try {
      // Create order in backend
      const orderRes = await api.post('/orders', { deliveryAddress: selectedAddress });
      const { order, razorpayOrderId, amount, currency, keyId } = orderRes.data;

      // Load Razorpay
      const options = {
        key: keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,
        currency,
        name: 'Stonari Official',
        description: 'Fashion Jewellery Order',
        order_id: razorpayOrderId,
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payment/verify', {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              orderId: order._id,
            });
            if (verifyRes.data.success) {
              navigate(`/order-confirmation/${order._id}`);
            }
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: { name: selectedAddress.name, contact: selectedAddress.phone },
        theme: { color: '#c9a96e' },
        modal: {
          ondismiss: async () => {
            await api.post('/payment/failure', { orderId: order._id });
            toast.error('Payment cancelled');
          },
        },
      };

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => { const rzp = new window.Razorpay(options); rzp.open(); };
        document.body.appendChild(script);
      } else {
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="page-header"><h1>Checkout</h1></div>
      <div className="container">
        <div className="checkout-layout">
          <div className="checkout-form">
            <div className="checkout-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Delivery Address</h2>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowAddressForm(true)}>
                  + Add New Address
                </button>
              </div>
              
              {addresses.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px', border: '1px dashed #ccc', borderRadius: '8px', textAlign: 'center', marginBottom: '20px' }}>
                  <p>No saved addresses.</p>
                  <button type="button" className="btn btn-primary" onClick={() => setShowAddressForm(true)}>Add Address</button>
                </div>
              ) : (
                <div className="addresses-list" style={{ marginBottom: '24px' }}>
                  {addresses.map(addr => (
                    <AddressCard 
                      key={addr._id} 
                      address={addr} 
                      selectable={true}
                      selected={selectedAddress?._id === addr._id}
                      onSelect={(address) => setSelectedAddress(address)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <button 
              className="btn btn-gold btn-full btn-lg" 
              onClick={handlePayment} 
              disabled={loading || addresses.length === 0 || !selectedAddress}
            >
              {loading ? 'Processing...' : `Pay ₹${total.toFixed(0)} via Razorpay`}
            </button>
          </div>

          <div className="checkout-summary">
            <h2>Order Summary</h2>
            {cart?.items?.map(item => item.product && (
              <div key={item.product._id} className="summary-item">
                <img src={item.product.images?.[0]} alt={item.product.name} />
                <div>
                  <p className="summary-item__name">{item.product.name}</p>
                  <p className="summary-item__qty">Qty: {item.quantity}</p>
                </div>
                <span>₹{(item.price * item.quantity).toFixed(0)}</span>
              </div>
            ))}
            <hr className="divider" />
            <div className="summary-row"><span>Subtotal</span><span>₹{subtotal.toFixed(0)}</span></div>
            <div className="summary-row"><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span></div>
            <hr className="divider" style={{margin:'12px 0'}} />
            <div className="summary-row" style={{fontWeight:700,fontSize:'1.1rem'}}><span>Total</span><span>₹{total.toFixed(0)}</span></div>
          </div>
        </div>
      </div>
      
      {showAddressForm && (
        <AddressForm 
          onSubmit={handleAddressSubmit} 
          onCancel={() => setShowAddressForm(false)} 
        />
      )}
    </div>
  );
};

export default CheckoutPage;
